/**
 * Pre-trade risk engine — phase 3 of broker integration.
 *
 * Runs client-side for immediate user feedback; the same rules are enforced
 * server-side by the pre-trade-risk Lambda which sits between the BAL and
 * the broker adapters. If the client bypasses it, the server-side check
 * rejects the order with a typed `risk_check_failed` error code.
 *
 * Rules covered (default thresholds; broker can override per account):
 *   R1  Buying power (account must have enough cash for BUY orders).
 *   R2  Position concentration (single instrument <= 25% of NAV).
 *   R3  Daily loss limit (realised + unrealised PnL cannot breach a
 *       configurable daily cap; blocks new orders once tripped).
 *   R4  Restricted symbols (per-broker blocklist; corp actions, sanctions).
 *   R5  Pattern day-trader cool-off (3 intraday round-trips in 5 days on
 *       the same symbol requires a cool-off window).
 *   R6  Minimum lot enforcement (NGX specific for bonds).
 *   R7  Wash-sale detection within 30 calendar days (soft warn, not hard
 *       block — surfaced to the user as a pink banner).
 *
 * Each check returns a `RiskFinding` with severity 'block' | 'warn' | 'info'
 * plus a human-readable reason so the ticket can render an auditable trace.
 */

export type Severity = 'block' | 'warn' | 'info';

export interface RiskFinding {
  code: 'R1' | 'R2' | 'R3' | 'R4' | 'R5' | 'R6' | 'R7';
  severity: Severity;
  message: string;
  /** Raw data for audit persistence. */
  detail?: Record<string, unknown>;
}

export interface RiskInput {
  side: 'BUY' | 'SELL';
  symbol: string;
  considerationKobo: bigint;
  buyingPowerKobo?: bigint;
  nav?: bigint;                 // account NAV in kobo
  currentPositionValueKobo?: bigint;
  dayPnlKobo?: bigint;
  dayLossLimitKobo?: bigint;
  restrictedSymbols?: string[];
  recentRoundTrips?: Array<{ symbol: string; at: number }>;
  recentSells?: Array<{ symbol: string; at: number }>;
  minLotConsiderationKobo?: bigint;
  now?: number;
}

const DAY_MS = 86_400_000;

export function evaluate(i: RiskInput): RiskFinding[] {
  const out: RiskFinding[] = [];
  const now = i.now ?? Date.now();

  // R1 — buying power (BUY only)
  if (i.side === 'BUY' && i.buyingPowerKobo !== undefined && i.considerationKobo > i.buyingPowerKobo) {
    out.push({
      code: 'R1', severity: 'block',
      message: 'Insufficient buying power for this order.',
      detail: { needed: i.considerationKobo.toString(), have: i.buyingPowerKobo.toString() },
    });
  }

  // R2 — concentration: existing + new (BUY) exposure <= 25% of NAV
  if (i.side === 'BUY' && i.nav && i.nav > 0n) {
    const newExposure = (i.currentPositionValueKobo ?? 0n) + i.considerationKobo;
    const limit = (i.nav * 25n) / 100n;
    if (newExposure > limit) {
      out.push({
        code: 'R2', severity: 'warn',
        message: 'Post-trade concentration would exceed 25% of NAV for this symbol.',
        detail: { newExposure: newExposure.toString(), limit: limit.toString() },
      });
    }
  }

  // R3 — daily loss limit tripped
  if (i.dayPnlKobo !== undefined && i.dayLossLimitKobo !== undefined) {
    if (i.dayPnlKobo <= -i.dayLossLimitKobo) {
      out.push({
        code: 'R3', severity: 'block',
        message: 'Daily loss limit reached. New orders blocked until tomorrow.',
        detail: { dayPnl: i.dayPnlKobo.toString(), limit: i.dayLossLimitKobo.toString() },
      });
    }
  }

  // R4 — restricted symbols
  if (i.restrictedSymbols?.includes(i.symbol)) {
    out.push({
      code: 'R4', severity: 'block',
      message: 'Symbol is on the broker\u2019s restricted list (sanctions/corp action).',
    });
  }

  // R5 — pattern day-trader: 3+ round trips in 5 trading days triggers cool-off
  if (i.recentRoundTrips && i.recentRoundTrips.length > 0) {
    const cutoff = now - 5 * DAY_MS;
    const rts = i.recentRoundTrips.filter((x) => x.symbol === i.symbol && x.at >= cutoff);
    if (rts.length >= 3) {
      out.push({
        code: 'R5', severity: 'warn',
        message: 'Pattern day-trader threshold: 3+ intraday round-trips on this symbol in 5 days.',
        detail: { rtCount: rts.length },
      });
    }
  }

  // R6 — minimum lot for bonds (min-lot consideration check)
  if (i.minLotConsiderationKobo !== undefined && i.considerationKobo < i.minLotConsiderationKobo) {
    out.push({
      code: 'R6', severity: 'block',
      message: 'Order consideration below NGX minimum lot size.',
      detail: { min: i.minLotConsiderationKobo.toString(), have: i.considerationKobo.toString() },
    });
  }

  // R7 — wash-sale warning: BUY within 30d of a SELL on the same symbol
  if (i.side === 'BUY' && i.recentSells && i.recentSells.length > 0) {
    const cutoff = now - 30 * DAY_MS;
    const hit = i.recentSells.find((x) => x.symbol === i.symbol && x.at >= cutoff);
    if (hit) {
      out.push({
        code: 'R7', severity: 'info',
        message: 'Wash-sale: tax-loss disallowed for BUYs within 30 days of a SELL on the same symbol.',
        detail: { lastSellAt: new Date(hit.at).toISOString() },
      });
    }
  }

  return out;
}

export function blocked(findings: RiskFinding[]): boolean {
  return findings.some((f) => f.severity === 'block');
}
